import { useCallback, useMemo, useState } from "react";

// Shared, reusable table-selection primitive (composed with ScrollableTable).
// A stable row id is mandatory. Selection is kept as an explicit id Set OR an
// "all filtered results" scope flag (so we never load thousands of rows just to select).
export function useTableSelection() {
  const [ids, setIds] = useState(() => new Set());
  const [allFilter, setAllFilter] = useState(false);

  const clear = useCallback(() => { setIds(new Set()); setAllFilter(false); }, []);
  const toggleRow = useCallback((id) => {
    setAllFilter(false);
    setIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const togglePage = useCallback((pageIds) => {
    setAllFilter(false);
    setIds((s) => {
      const n = new Set(s);
      const all = pageIds.length > 0 && pageIds.every((id) => n.has(id));
      if (all) pageIds.forEach((id) => n.delete(id)); else pageIds.forEach((id) => n.add(id));
      return n;
    });
  }, []);

  return { ids, allFilter, setAllFilter, clear, toggleRow, togglePage, setIds };
}

// Deterministic CSV of the SELECTED rows using the VISIBLE/business columns only.
// UTF-8 (BOM for Excel), explicit headers, RFC-4180 quoting. Returns the CSV string
// (the caller triggers the download) so it is unit-testable without a DOM.
export function buildSelectionCsv(rows, columns) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.get(r))).join(",")).join("\n");
  return `\uFEFF${header}\n${body}`;
}

export function downloadCsv(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Convenience for pages that hold the full filtered list client-side.
export function useSelectionMeta(ids, allFilter, filteredIds) {
  return useMemo(() => ({
    count: allFilter ? filteredIds.length : ids.size,
    isEmpty: !allFilter && ids.size === 0,
  }), [ids, allFilter, filteredIds]);
}
